import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import {
  fetchBlogWithComments,
  fetchRelatedBlogs,
  selectBlog,
  selectRelatedBlogs,
  selectBlogDetailsStatus,
  selectBlogDetailsError,
  clearBlogDetails,
} from '../redux/blogdetailsSlice';

const Blogdetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Redux state
  const blog = useSelector(selectBlog);
  const relatedBlogs = useSelector(selectRelatedBlogs);
  const blogStatus = useSelector(selectBlogDetailsStatus);
  const blogError = useSelector(selectBlogDetailsError);

  // Local state for comment form
  const [commentName, setCommentName] = useState('');
  const [commentEmail, setCommentEmail] = useState('');
  const [commentText, setCommentText] = useState('');
  const [saveInfo, setSaveInfo] = useState(false);

  // Local state for comments
  const [comments, setComments] = useState([]);
  const [commentStatus, setCommentStatus] = useState('idle');
  const [visibleComments, setVisibleComments] = useState(8);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');

  // Blog search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Product tags state
  const [productTags, setProductTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Function to render HTML content safely
  const renderHTMLContent = (content) => {
    if (!content) return null;
    
    // Process content to handle line breaks and format properly
    let processedContent = content;
    
    // Replace \r\n\r\n with </p><p> for proper paragraph breaks
    processedContent = processedContent.replace(/\r\n\r\n/g, '</p><p>');
    
    // Replace \r\n with <br /> for single line breaks
    processedContent = processedContent.replace(/\r\n/g, '<br />');
    
    // Ensure proper paragraph wrapping
    if (!processedContent.startsWith('<p>')) {
      processedContent = `<p>${processedContent}</p>`;
    }
    
    return { __html: processedContent };
  };

  // Check login status on mount and whenever localStorage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const customerId = localStorage.getItem('customer_id');
      
      // console.log('=== LOGIN STATUS CHECK ===');
      // console.log('authToken:', token ? 'Present' : 'Missing');
      // console.log('refreshToken:', refreshToken ? 'Present' : 'Missing');
      // console.log('customer_id:', customerId);
      
      setIsLoggedIn(!!token);
      
      if (!token) {
        setDebugInfo('No token found');
      } else {
        setDebugInfo(`Token present (length: ${token.length})`);
      }
    };
    
    checkLoginStatus();
    
    // Listen for storage changes (in case login happens in another tab)
    window.addEventListener('storage', checkLoginStatus);
    
    return () => window.removeEventListener('storage', checkLoginStatus);
  }, []);

  // Fetch blog data on mount
  useEffect(() => {
    if (id) {
      dispatch(fetchBlogWithComments(id));
    }

    return () => {
      dispatch(clearBlogDetails());
    };
  }, [dispatch, id]);

  // Fetch related blogs when blog data is loaded
  useEffect(() => {
    if (blog?.tags) {
      dispatch(fetchRelatedBlogs(blog.tags));
    }
  }, [dispatch, blog]);

  // Fetch comments directly from API
  useEffect(() => {
    if (!id) return;

    fetch(`https://evercorenutrition.com/ev_admin/api/blogs/${id}/comments/`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch comments');
        return res.json();
      })
      .then((data) => {
        const commentsList = data?.results?.comments || [];
        setComments(commentsList);
      })
      .catch((err) => {
        console.error('Error fetching comments:', err);
        setComments([]);
      });
  }, [id]);

  // Fetch product tags from API
  useEffect(() => {
    fetchProductTags();
  }, []);

  const fetchProductTags = async () => {
    setTagsLoading(true);
    try {
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/blogs/');
      if (response.ok) {
        const data = await response.json();
        
        // Extract unique tags from all blogs
        const allTags = [];
        if (data.results && Array.isArray(data.results)) {
          data.results.forEach(blog => {
            if (blog.tags) {
              // Split tags by comma and trim whitespace
              const tags = blog.tags.split(',').map(tag => tag.trim());
              tags.forEach(tag => {
                if (tag && !allTags.includes(tag)) {
                  allTags.push(tag);
                }
              });
            }
          });
        }
        
        setProductTags(allTags.slice(0, 10)); // Limit to 10 tags
      }
    } catch (error) {
      console.error('Error fetching product tags:', error);
      // Fallback tags
      setProductTags([
        'protein', 'collagen', 'vitamins', 'supplements',
        'fitness', 'wellness', 'nutrition', 'health',
        'recovery', 'immunity'
      ]);
    } finally {
      setTagsLoading(false);
    }
  };

  // Blog search functionality
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length > 1) {
        fetchSearchSuggestions(searchQuery);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const fetchSearchSuggestions = async (query) => {
    setIsSearching(true);
    setShowSuggestions(true);
    
    try {
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/blogs/search/suggest/?q=${encodeURIComponent(query)}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch suggestions');
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.suggestions)) {
        setSearchSuggestions(data.suggestions);
      } else {
        setSearchSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching blog suggestions:', error);
      setSearchSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInput = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSuggestionClick = (blogId) => {
    navigate(`/Blogdetails/${blogId}`);
    setSearchQuery('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleSearchFocus = () => {
    if (searchSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle comment submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setCommentStatus('loading');

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setCommentStatus('failed');
        alert('Please login to post a comment');
        return;
      }

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/blogs/${id}/comments/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            comment: commentText,
          }),
        }
      );

      // console.log('Response status:', response.status);

      if (response.ok) {
        const newComment = await response.json();
        // console.log('Success! Comment posted:', newComment);
        
        // Refresh comments list
        const commentsRes = await fetch(`https://evercorenutrition.com/ev_admin/api/blogs/${id}/comments/`);
        if (commentsRes.ok) {
          const commentsData = await commentsRes.json();
          setComments(commentsData?.results?.comments || []);
        }

        setCommentStatus('succeeded');
        
        // Clear form
        setCommentText('');
        if (!saveInfo) {
          setCommentName('');
          setCommentEmail('');
        }

        setTimeout(() => setCommentStatus('idle'), 3000);
        
      } else if (response.status === 401) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const refreshResponse = await fetch('https://evercorenutrition.com/ev_admin/api/token/refresh/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ refresh: refreshToken }),
            });

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              localStorage.setItem('authToken', refreshData.access);
              alert('Session refreshed. Please try posting your comment again.');
            } else {
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('customer_id');
              alert('Your session has expired. Please login again.');
            }
          } catch (refreshErr) {
            console.error('Refresh error:', refreshErr);
            alert('Session expired. Please login again.');
          }
        } else {
          localStorage.removeItem('authToken');
          alert('Your session has expired. Please login again.');
        }
        setCommentStatus('failed');
      } else {
        const errorText = await response.text();
        console.error('Failed to post comment:', errorText);
        setCommentStatus('failed');
      }
      
    } catch (err) {
      console.error('Error posting comment:', err);
      setCommentStatus('failed');
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Get blog image with proper path
  const getBlogImage = (blog) => {
    if (blog?.featured_image) {
      return blog.featured_image.startsWith('http')
        ? blog.featured_image
        : `https://evercorenutrition.com${blog.featured_image}`;
    }
    return '/assets/images/Yeast_Protein.png';
  };

  // Add a manual token setter for testing (only in development)
  const setTestToken = () => {
    const testToken = prompt('Enter your auth token:');
    if (testToken) {
      localStorage.setItem('authToken', testToken);
      setIsLoggedIn(true);
      setDebugInfo('Test token set manually');
    }
  };

  // Clear token for testing
  const clearTestToken = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('customer_id');
    setIsLoggedIn(false);
    setDebugInfo('Token cleared');
  };

  // Loading state
  if (blogStatus === 'loading') {
    return (
      <>
        <Navbar />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Error state
  if (blogStatus === 'failed') {
    return (
      <>
        <Navbar />
        <div className="alert alert-danger m-5" role="alert">
          Error loading blog: {blogError}
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      {/* Breadcrumb Section */}
      <section className="breadcrumb-section pt-0 mt-5">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <a href="/">
                        <i className="fa-solid fa-house"></i>
                      </a>
                    </li>
                    <li className="breadcrumb-item">
                      <a href="/Blog" style={{ textDecoration: 'none', color: '#000' }}>
                        Blog
                      </a>
                    </li>
                    <li className="breadcrumb-item active">Blog Details</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Details Section */}
<section className="blog-section section-b-space">
  <div className="container-fluid-lg">
    <div className="row g-sm-4 g-3">
                 <div className="col-xxl-9 col-xl-8 col-lg-7 ratio_50" style={{ paddingLeft: '16px', paddingRight: '16px' }}>

              {/* Blog Image */}
              <div className="blog-detail-image rounded-3 mb-4">
<img
  src={getBlogImage(blog)}
  className="bg-img blur-up lazyload"
  alt={blog?.title || "Blog image"}
  style={{
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'contain',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', // default light gradient
  }}
  onError={(e) => {
    e.target.onerror = null;
    e.target.src = '/assets/images/Yeast_Protein.png';
  }}
  onLoad={(e) => {
    const img = e.target;
    
    // Create a temporary canvas to extract dominant color
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = img.naturalWidth || 300;
    canvas.height = img.naturalHeight || 200;
    
    try {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

      // Simple dominant color extraction (average of top-left, center, bottom-right)
      const getColor = (x, y) => {
        const index = (y * canvas.width + x) * 4;
        return {
          r: imageData[index],
          g: imageData[index + 1],
          b: imageData[index + 2]
        };
      };

      const color1 = getColor(Math.floor(canvas.width * 0.1), Math.floor(canvas.height * 0.2));
      const color2 = getColor(Math.floor(canvas.width * 0.8), Math.floor(canvas.height * 0.7));

      // Create light gradient from dominant colors
      const lightGradient = `linear-gradient(135deg, 
        rgba(${color1.r}, ${color1.g}, ${color1.b}, 0.12) 0%, 
        rgba(${color2.r}, ${color2.g}, ${color2.b}, 0.08) 100%
      )`;

      img.style.background = lightGradient;
    } catch (err) {
      console.warn("Could not extract image color:", err);
      // Fallback to soft light gradient
      img.style.background = 'linear-gradient(135deg, #f1f3f5 0%, #e6e9ef 100%)';
    }
  }}
/>
                <div className="blog-image-contain">
                  <h2>{blog?.title || 'Blog Post'}</h2>
                  <ul className="contain-comment-list">
                    <li>
                      <div className="user-list">
                        <i className="fa-regular fa-user"></i>
                        <span>{blog?.author || 'Evercore'}</span>
                      </div>
                    </li>
                    <li>
                      <div className="user-list">
                        <i className="fa-regular fa-calendar"></i>
                        <span>{formatDate(blog?.publication_date || blog?.created_at || new Date())}</span>
                      </div>
                    </li>
                    <li>
                      <div className="user-list">
                        <i className="fa-regular fa-message"></i>
                        <span>
                          {comments.length || 0} Comment{comments.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Blog Content - Rendered as HTML */}
              <div className="blog-detail-contain">
                {blog?.content ? (
                  <div 
                    className="blog-content"
                    dangerouslySetInnerHTML={renderHTMLContent(blog.content)}
                    style={{
                      fontSize: '16px',
                      lineHeight: '1.8',
                      color: '#333',
                    }}
                  />
                ) : (
                  <div className="blog-content">
                    <p>No content available for this blog post.</p>
                  </div>
                )}
              </div>

              {/* Comments List */}
              <div className="comment-box overflow-hidden">
                <div className="leave-title">
                  <h3>Comments</h3>
                </div>

<div className="user-comment-box">
  <ul>
    {comments.length > 0 ? (
      comments.slice(0, visibleComments).map((comment, index) => (
        <li key={comment.id || index}>
          <div
            className={`user-box ${
              index < Math.min(visibleComments, comments.length) - 1 ? 'border-color' : ''
            }`}
          >
            <div className="user-image">
              <img
                src="/assets/images/user.png"
                className="img-fluid blur-up lazyload"
                alt="user"
                style={{ width: '50px', height: '50px', borderRadius: '50%' }}
              />
              <div className="user-name">
                <h6>{comment.created_at ? formatDate(comment.created_at) : '—'}</h6>
                <h5 className="text-content">{comment.user_name || 'Anonymous'}</h5>
              </div>
            </div>
            <div className="user-contain">
              <p>{comment.comment}</p>
            </div>
          </div>
        </li>
      ))
    ) : (
      <li>
        <p className="text-content">No comments yet. Be the first to comment!</p>
      </li>
    )}
  </ul>

  {/* View More / View Less buttons */}
  {comments.length > 8 && (
    <div style={{ textAlign: 'center', marginTop: '16px', marginBottom: '8px' }}>
      {visibleComments < comments.length ? (
        <button
          onClick={() => setVisibleComments(prev => prev + 8)}
          style={{
            padding: '10px 28px',
            background: '#000',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          View More ({comments.length - visibleComments} remaining)
        </button>
      ) : (
        <button
          onClick={() => setVisibleComments(8)}
          style={{
            padding: '10px 28px',
            background: 'transparent',
            color: '#333',
            border: '2px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Show Less
        </button>
      )}
    </div>
  )}
</div>
              </div>

              {/* Leave Comment Form */}
              <div className="leave-box">
                <div className="leave-title mt-0">
                  <h3>Leave Comment</h3>
                </div>

                <div className="leave-comment">
                  <div className="comment-notes">
                    {/* <p className="text-content mb-4">
                      Your email address will not be published. Required fields are marked
                    </p> */}
                  </div>

                  {!isLoggedIn ? (
                    <div className="alert alert-info">
                      <p><strong>Please <a href="/login" className="theme-color">login</a> to post a comment.</strong></p>
                      
                    </div>
                  ) : (
                    <form onSubmit={handleCommentSubmit}>
                      <div className="row g-3">
                        <div className="col-xxl-4 col-lg-12 col-sm-6">
                          <div className="blog-input">
                            <input
                              type="hidden"
                              className="form-control"
                              placeholder="Full Name"
                              value={commentName}
                              onChange={(e) => setCommentName(e.target.value)}
                              
                            />
                          </div>
                        </div>

                        <div className="col-xxl-4 col-lg-12 col-sm-6">
                          <div className="blog-input">
                            <input
                              type="hidden"
                              className="form-control"                             
                              value={commentEmail}
                              onChange={(e) => setCommentEmail(e.target.value)}
                              
                            />
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="blog-input">
                            <textarea
                              className="form-control"
                              rows="4"
                              placeholder="Comments"
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              required
                            ></textarea>
                          </div>
                        </div>
                      </div>

                      {/* <div className="form-check d-flex mt-4 p-0">
                        <input
                          className="checkbox_animated"
                          type="checkbox"
                          checked={saveInfo}
                          onChange={(e) => setSaveInfo(e.target.checked)}
                          id="flexCheckDefault"
                        />
                        <label
                          className="form-check-label text-content"
                          htmlFor="flexCheckDefault"
                        >
                          <span className="color color-1">
                            Save my name, email, and website in this browser for the next time I comment.
                          </span>
                        </label>
                      </div> */}

                      <button
                        type="submit"
                        className="btn btn-animation ms-xxl-auto mt-xxl-0 mt-3 btn-md fw-bold"
                        disabled={commentStatus === 'loading'}
                        style={{
                          backgroundColor: '#000',
                          color: '#fff',
                          padding: '10px 25px',
                          borderRadius: '5px',
                          border: 'none'
                        }}
                      >
                        {commentStatus === 'loading' ? 'Posting...' : 'Post Comment'}
                      </button>
                    </form>
                  )}

                  {commentStatus === 'succeeded' && (
                    <div className="alert alert-success mt-3">Comment posted successfully!</div>
                  )}
                  {commentStatus === 'failed' && (
                    <div className="alert alert-danger mt-3">
                      Failed to post comment. Please make sure you're logged in with a valid account.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar with Blog Search */}
            <div className="col-xxl-3 col-xl-4 col-lg-5 d-lg-block d-none">
              <div className="left-sidebar-box">
                <div className="left-search-box">
                  <div className="search-box" style={{ position: 'relative' }}>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search blogs..."
                      value={searchQuery}
                      onChange={handleSearchInput}
                      onFocus={handleSearchFocus}
                      onBlur={handleSearchBlur}
                      style={{ paddingRight: '35px' }}
                    />
                    {isSearching && (
                      <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Search Suggestions Dropdown */}
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}>
                        {searchSuggestions.map((suggestion) => (
                          <div
                            key={suggestion.id}
                            onClick={() => handleSuggestionClick(suggestion.id)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                              {suggestion.title}
                            </h6>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {showSuggestions && searchQuery.length > 1 && searchSuggestions.length === 0 && !isSearching && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '15px',
                        textAlign: 'center',
                        color: '#666',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}>
                        No blogs found
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="accordion left-accordion-box"
                  id="accordionPanelsStayOpenExample"
                >
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="panelsStayOpen-headingOne">
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#panelsStayOpen-collapseOne"
                      >
                        Recent Post
                      </button>
                    </h2>
                    <div
                      id="panelsStayOpen-collapseOne"
                      className="accordion-collapse collapse show"
                    >
                      <div className="accordion-body pt-0">
                        <div className="recent-post-box">
                          {relatedBlogs?.length > 0 ? (
                            relatedBlogs.slice(0, 2).map((relatedBlog) => (
                              <div className="recent-box d-flex gap-3 mb-3" key={relatedBlog.id}>
                                <a href={`/Blogdetails/${relatedBlog.id}`} className="recent-image" style={{ flexShrink: 0 }}>
                                  <img
                                    src={
                                      relatedBlog.featured_image
                                        ? relatedBlog.featured_image.startsWith('http')
                                          ? relatedBlog.featured_image
                                          : `https://evercorenutrition.com${relatedBlog.featured_image}`
                                        : '/assets/images/Yeast_Protein.png'
                                    }
                                    className="img-fluid blur-up lazyload"
                                    alt={relatedBlog.title}
                                    style={{ 
                                      width: '70px', 
                                      height: '70px', 
                                      objectFit: 'cover',
                                      borderRadius: '8px'
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '/assets/images/Yeast_Protein.png';
                                    }}
                                  />
                                </a>

                                <div className="recent-detail flex-grow-1">
                                  <a href={`/Blogdetails/${relatedBlog.id}`} style={{ textDecoration: 'none' }}>
                                    <h5 className="recent-name" style={{ 
                                      fontSize: '14px', 
                                      fontWeight: '600',
                                      marginBottom: '5px',
                                      color: '#000'
                                    }}>
                                      {relatedBlog.title.length > 50
                                        ? relatedBlog.title.substring(0, 50) + '...'
                                        : relatedBlog.title}
                                    </h5>
                                  </a>
                                  <h6 style={{ fontSize: '11px', color: '#666', margin: 0 }}>
                                    {formatDate(relatedBlog.publication_date || relatedBlog.created_at)}
                                  </h6>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-muted small">No recent posts</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h2 className="accordion-header" id="panelsStayOpen-headingThree">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#panelsStayOpen-collapseThree"
                      >
                        Product Tags
                      </button>
                    </h2>
                    <div
                      id="panelsStayOpen-collapseThree"
                      className="accordion-collapse collapse show"
                    >
                      <div className="accordion-body pt-0">
                        <div className="product-tags-box">
                          {tagsLoading ? (
                            <div className="text-center py-3">
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading tags...</span>
                              </div>
                            </div>
                          ) : (
                            <ul className="d-flex flex-wrap gap-2" style={{ paddingLeft: 0 }}>
                              {productTags.length > 0 ? (
                                productTags.map((tag, index) => (
                                  <li key={index} style={{ listStyle: 'none' }}>
                                    <a 
                                      href={`/Blog?tag=${encodeURIComponent(tag)}`} 
                                      style={{ 
                                        textDecoration: 'none',
                                        backgroundColor: '#f0f0f0',
                                        color: '#333',
                                        padding: '5px 12px',
                                        borderRadius: '20px',
                                        fontSize: '13px',
                                        display: 'inline-block',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                    >
                                      {tag}
                                    </a>
                                  </li>
                                ))
                              ) : (
                                <li style={{ listStyle: 'none', color: '#666' }}>No tags available</li>
                              )}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />

      {/* Add custom CSS for blog content */}
      <style>{`

        @media (max-width: 768px) {
    .container-fluid-lg {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
    
    .blog-section .row {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .blog-section .col-xxl-9 {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .blog-detail-image,
    .blog-detail-contain,
    .comment-box,
    .leave-box {
      padding-left: 4px;
      padding-right: 4px;
    }

    .blog-content {
      padding: 0 4px;
    }
  }

        .blog-content p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
        }
        
        .blog-content h1, .blog-content h2, .blog-content h3 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .blog-content ul, .blog-content ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        
        .blog-content li {
          margin-bottom: 0.5rem;
        }
        
        .blog-content strong {
          font-weight: 600;
          color: #000;
        }
        
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1.5rem 0;
        }
        
        .blog-content blockquote {
          border-left: 4px solid #2b7e2b;
          padding-left: 1.5rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #555;
        }
      `}</style>
    </>
  );
};

export default Blogdetails;